<template>
  <div class="attendee-scan" :class="{ 'attendee-scan--two-actions': !!live }">
    <div class="attendee-scan__header">
      <h1 class="attendee-scan__title">Scan in/out</h1>
      <p class="attendee-scan__subtitle">Sign in and out of session rooms to verify your attendance and earn CE credit.</p>
    </div>

    <!-- Announces each scan result: the scan happens through the camera, so a
         screen reader user has nothing else confirming it worked. -->
    <div class="attendee-scan__sr-only" aria-live="polite" role="status">{{ announcement }}</div>

    <!-- Keys on every same-tag v-if branch: FontAwesome swaps each <i> for an
         <svg> Vue does not track, so a reused element would keep (or pile up)
         the previous branch's icon. -->
    <div v-if="status === 'loading'" key="loading" class="attendee-scan__skeleton" aria-hidden="true">
      <div class="attendee-scan__skeleton-card"></div>
      <div class="attendee-scan__skeleton-card attendee-scan__skeleton-card--short"></div>
    </div>

    <div v-else-if="status === 'error'" key="error" class="attendee-scan__load-error" role="alert">
      <i class="fa-regular fa-triangle-exclamation" aria-hidden="true"></i>
      <span>Couldn't load your sign-ins.</span>
      <button type="button" class="attendee-scan__retry" @click="retry">Retry</button>
    </div>

    <div v-else key="ready" class="attendee-scan__body">
      <scan-result v-if="result" :result="result" />

      <visit-card
        v-if="live"
        :visit="live"
        variant="live"
        :now="clockNow"
        :primary-label="stale.length ? 'In the room now' : 'In the room'"
      />

      <ul v-if="live && !stale.length" key="hint" class="attendee-scan__group">
        <li class="attendee-scan__row">
          <span class="attendee-scan__row-icon" aria-hidden="true">
            <i class="fa-regular fa-arrow-right-from-bracket"></i>
          </span>
          <div class="attendee-scan__row-text">
            <b class="attendee-scan__row-title">Leaving?</b>
            <div class="attendee-scan__row-meta">
              <template v-if="live.room">Scan the same code at the door of {{ live.room }}</template>
              <template v-else>Scan the same session code</template>
            </div>
          </div>
        </li>
        <li class="attendee-scan__row">
          <span class="attendee-scan__row-icon" aria-hidden="true">
            <i class="fa-regular fa-plus"></i>
          </span>
          <div class="attendee-scan__row-text">
            <b class="attendee-scan__row-title">Going to another session?</b>
            <div class="attendee-scan__row-meta">Sign in there too — we'll keep both timers</div>
          </div>
        </li>
      </ul>

      <visit-card
        v-for="visit in stale"
        :key="visit.sessionId"
        :visit="visit"
        variant="stale"
        :now="clockNow"
        @sign-out="openScanner({ type: 'sign-out', sessionName: $event.sessionName, room: $event.room })"
      />

      <!-- The full how-to is for someone who hasn't scanned yet today; once
           there is history it shrinks to a line and "Today" moves up. -->
      <template v-if="!visits.length && !result && !today.length">
        <div key="hero" class="attendee-scan__hero">
          <span class="attendee-scan__hero-icon" aria-hidden="true">
            <i class="fa-regular fa-qrcode"></i>
          </span>
          <h2 class="attendee-scan__hero-title">Sign in to a session</h2>
          <p class="attendee-scan__hero-text">Your time in the room is recorded for your CE credit.</p>
        </div>

        <section key="steps" aria-labelledby="scan-how-title">
          <h2 id="scan-how-title" class="attendee-scan__group-title">How it works</h2>
          <ol class="attendee-scan__group">
            <li class="attendee-scan__row">
              <span class="attendee-scan__row-icon" aria-hidden="true">
                <i class="fa-regular fa-arrow-right-to-bracket"></i>
              </span>
              <div class="attendee-scan__row-text">
                <b class="attendee-scan__row-title">Arrive</b>
                <div class="attendee-scan__row-meta">Scan the QR code at the room door</div>
              </div>
            </li>
            <li class="attendee-scan__row">
              <span class="attendee-scan__row-icon" aria-hidden="true">
                <i class="fa-regular fa-arrow-right-from-bracket"></i>
              </span>
              <div class="attendee-scan__row-text">
                <b class="attendee-scan__row-title">Leave</b>
                <div class="attendee-scan__row-meta">Scan the same code on your way out</div>
              </div>
            </li>
            <li class="attendee-scan__row">
              <span class="attendee-scan__row-icon" aria-hidden="true">
                <i class="fa-regular fa-clock-rotate-left"></i>
              </span>
              <div class="attendee-scan__row-text">
                <b class="attendee-scan__row-title">Stepped out?</b>
                <div class="attendee-scan__row-meta">Scan again when you're back — the timer picks up where it stopped</div>
              </div>
            </li>
          </ol>
        </section>
      </template>

      <p v-else-if="!visits.length && !result" key="lead" class="attendee-scan__lead">
        Scan the code at the room door when you arrive and when you leave.
      </p>

      <today-list :visits="today" :label="todayLabel" :has-live="!!live" />

      <div v-if="today.length && !live" key="tip" class="attendee-scan__tip">
        <i class="fa-regular fa-circle-info" aria-hidden="true"></i>
        Stepped out? Scan again — the timer picks up where it stopped.
      </div>
    </div>

    <div class="attendee-scan__dock">
      <button
        v-if="live"
        ref="primaryAction"
        key="action-sign-out"
        type="button"
        class="attendee-scan__action attendee-scan__action--out"
        :disabled="status !== 'ready' || isSending"
        @click="openScanner({ type: 'sign-out', sessionName: live.sessionName, room: live.room })"
      >
        <i class="fa-regular fa-qrcode" aria-hidden="true"></i>
        <span class="attendee-scan__action-label">
          Scan to sign out
          <span class="attendee-scan__action-sub">{{ live.sessionName }}</span>
        </span>
      </button>

      <button
        v-else
        ref="primaryAction"
        key="action-sign-in"
        type="button"
        class="attendee-scan__action attendee-scan__action--in"
        :disabled="status !== 'ready' || isSending"
        @click="openScanner({ type: 'sign-in' })"
      >
        <i class="fa-regular fa-qrcode" aria-hidden="true"></i>
        <span class="attendee-scan__action-label">Scan to sign in</span>
      </button>

      <button
        v-if="live"
        key="action-another"
        type="button"
        class="attendee-scan__action attendee-scan__action--secondary"
        :disabled="status !== 'ready' || isSending"
        @click="openScanner({ type: 'sign-in' })"
      >
        <i class="fa-regular fa-plus" aria-hidden="true"></i>
        Sign in to another session
      </button>
    </div>

    <scanner-sheet
      v-if="intent"
      :intent="intent"
      :busy="isSending"
      @decode="sendToken"
      @error="onCameraError"
      @close="closeScanner"
    />
  </div>
</template>

<script>
import common from "../../helpers/common";
import scanAPI from "../../api/scanAPI";
import { createServerClock, normalizeVisit, onAppResumed, orderVisits } from "./scan-clock";
import VisitCard from "./visit-card";
import ScanResult from "./scan-result";
import TodayList from "./today-list";
import ScannerSheet from "./scanner-sheet";

const toMs = (seconds) => (seconds != null ? seconds * 1000 : null);

export default {
  components: {
    VisitCard,
    ScanResult,
    TodayList,
    ScannerSheet,
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      // 'loading' until the first answer, then 'ready'; 'error' only if that first load failed.
      status: 'loading',
      visits: [],
      today: [],
      todayLabel: '',
      clock: createServerClock(),
      clockNow: Date.now(),
      timerId: null,
      // Only the newest refresh may update the page (app resume, visibility
      // and a finished scan can each start one).
      fetchSeq: 0,
      clockSynced: false,
      // What the open scanner is for; null while it is closed.
      intent: null,
      isSending: false,
      result: null,
      announcement: '',
      stopResumed: null,
    }
  },
  computed: {
    ordered() {
      return orderVisits(this.visits, this.clockNow);
    },

    live() {
      return this.ordered.live;
    },

    stale() {
      return this.ordered.stale;
    },
  },
  watch: {
    result(result) {
      if (!result) {
        return;
      }

      this.announcement = result.type === 'error'
        ? `Could not sign you in or out. ${result.message}`
        : `${result.type === 'sign-in' ? 'Signed in' : 'Signed out'}. ${result.sessionName}`;
    },
  },
  mounted() {
    common.setHeaderText('Scan in/out');
    this.fetchState();

    this.timerId = setInterval(this.tick, 1000);

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.stopResumed = onAppResumed(this.fetchState);
  },
  beforeDestroy() {
    clearInterval(this.timerId);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.stopResumed) {
      this.stopResumed();
    }
  },
  methods: {
    // Visits and today's list come from the server every time, so timers
    // survive reloads, app restarts and route changes. A failed refresh keeps
    // what is on screen; only a failed first load shows the error.
    fetchState() {
      const seq = ++this.fetchSeq;
      const sentAt = Date.now();

      // Sync on the `active` answer itself, at the request's midpoint: waiting
      // for the slower `today` call would skew the offset and step timers back.
      const active = scanAPI.active(this.attendeeId).then((response) => {
        if (seq === this.fetchSeq) {
          this.clock.sync(response.data.server_now, (sentAt + Date.now()) / 2);

          // The first sync replaces the device's own time outright: it may
          // run ahead of the server, and tick() never moves backwards.
          if (!this.clockSynced) {
            this.clockSynced = true;
            this.clockNow = this.clock.now();
          }
        }

        return response;
      });

      return Promise.all([active, scanAPI.today(this.attendeeId)])
        .then(([activeResponse, todayResponse]) => {
          if (seq !== this.fetchSeq) {
            return;
          }

          this.tick();
          this.visits = (activeResponse.data.data || []).map(normalizeVisit);
          this.today = todayResponse.data.data || [];
          this.todayLabel = todayResponse.data.today_label || '';
          this.status = 'ready';
        })
        .catch(() => {
          if (seq === this.fetchSeq && this.status !== 'ready') {
            this.status = 'error';
          }
        });
    },

    // Timers only move forward: a re-sync that lands a little earlier must
    // not make a running timer visibly step back.
    tick() {
      this.clockNow = Math.max(this.clockNow, this.clock.now());
    },

    retry() {
      this.status = 'loading';
      this.fetchState();
    },

    onVisibilityChange() {
      if (!document.hidden && this.status === 'ready') {
        this.fetchState();
      }
    },

    openScanner(intent) {
      this.result = null;
      this.intent = intent;
    },

    closeScanner() {
      this.intent = null;
      this.$nextTick(() => {
        if (this.$refs.primaryAction) {
          this.$refs.primaryAction.focus({ preventScroll: true });
        }
      });
    },

    onCameraError(message) {
      this.result = { type: 'error', message };
    },

    sendToken(token) {
      // The attendee may close this sheet and open another while the scan is
      // on its way; only the sheet that sent it closes when it settles.
      const sentFrom = this.intent;

      this.isSending = true;

      scanAPI.scan(this.attendeeId, token)
        .then((res) => {
          const data = res.data.data;

          this.result = {
            type: data.action,
            sessionName: data.session_name,
            time: data.time,
            earlierMs: (data.earlier_seconds || 0) * 1000,
            // The session total when known (several visits add up), else this visit.
            totalMs: toMs(data.session_total_seconds != null ? data.session_total_seconds : data.time_in_room_seconds),
            thisVisitMs: toMs(data.time_in_room_seconds),
          };

          if (typeof navigator.vibrate === 'function') {
            navigator.vibrate(40);
          }

          // Show the result now; the lists refresh in the background.
          this.fetchState();
        })
        .catch((err) => {
          this.result = {
            type: 'error',
            message: err?.response?.data?.flash
              || 'This QR code could not be processed. Please try again or ask event staff for help.',
          };
        })
        .finally(() => {
          this.isSending = false;

          if (this.intent === sentFrom) {
            this.closeScanner();
          }
        });
    },
  },
}
</script>

<!-- Unscoped: defines the page's color roles once (--scan-*), which the
     child components read. On the attendee-dashboard theming branch these are
     re-pointed to the theme tokens. -->
<style lang="scss">
.attendee-scan {
  --scan-live: #2b8a3e;
  --scan-live-tint: #ebfbee;
  --scan-live-border: #b2f2bb;
  --scan-warn: #8a5a12;
  --scan-warn-tint: #fbf1df;
  --scan-warn-border: rgba(183, 121, 31, .45);
  --scan-brand: var(--attendee-brand-color);
  --scan-ink: var(--attendee-text-primary-color);
  --scan-muted: var(--attendee-text-secondary-color);
  --scan-danger: #c92a2a;
  // The camera sheet stays dark in every theme, like any camera viewfinder.
  --scan-sheet-bg: #0d0c0a;
  --scan-sheet-ink: #fff;
  --scan-sheet-veil: rgb(0 0 0 / 60%);
  --scan-sheet-shadow: rgb(0 0 0 / 45%);
  --scan-sheet-dim: rgb(0 0 0 / 42%);
  --scan-sheet-glass: rgb(255 255 255 / 14%);
  --scan-sheet-glass-soft: rgb(255 255 255 / 10%);
  --scan-sheet-hint: rgb(255 255 255 / 88%);
  --scan-sheet-track: rgb(255 255 255 / 30%);
  --scan-dock-height: 96px;

  max-width: 640px;
  color: var(--scan-ink);

  // The layout already reserves room for the bottom menu; the dock sits on top
  // of that menu plus the home-indicator inset, so the content clears both.
  @media (max-width: 900px) {
    max-width: none;
    padding: 12px var(--attendee-gap-md) calc(var(--scan-dock-height) + env(safe-area-inset-bottom) + var(--attendee-gap-md));
  }

  &--two-actions {
    --scan-dock-height: 150px;
  }

  &__header {
    margin-bottom: var(--attendee-gap-md);

    @media (max-width: 900px) {
      display: none;
    }
  }

  &__title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
  }

  &__subtitle {
    margin: 4px 0 0;
    color: var(--scan-muted);
    font-size: 14px;
  }

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  // Grouped list: icon + text rows in one card, the shared look of the
  // "How it works", "Leaving?" and "Today" blocks.
  &__group {
    margin: 0;
    padding: 0;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--scan-ink) 9%, transparent);
    border-radius: calc(var(--attendee-card-border-radius) + 4px);
    background-color: var(--attendee-card-color);
    list-style: none;
  }

  &__group-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 6px 6px 8px;
    color: var(--scan-muted);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  &__group-date {
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
  }

  &__row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;

    // Divider starts after the icon column, like native grouped lists.
    & + &::before {
      position: absolute;
      top: 0;
      right: 0;
      left: 60px;
      border-top: 1px solid color-mix(in srgb, var(--scan-ink) 9%, transparent);
      content: '';
    }
  }

  &__row-icon {
    display: grid;
    flex: 0 0 34px;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background-color: color-mix(in srgb, var(--scan-brand) 12%, transparent);
    color: var(--scan-brand);
    font-size: 16px;

    &--done {
      border-radius: 50%;
      background-color: var(--scan-live-tint);
      color: var(--scan-live);
      font-size: 14px;
    }
  }

  &__row-text {
    flex: 1;
    min-width: 0;
  }

  &__row-title {
    display: block;
    color: var(--scan-ink);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  &__row-meta {
    margin-top: 2px;
    color: var(--scan-muted);
    font-size: 12.5px;
    line-height: 1.4;
  }

  &__row-value {
    color: var(--scan-ink);
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__group-sum {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 14px;
    border-top: 1px solid color-mix(in srgb, var(--scan-ink) 9%, transparent);
    background-color: color-mix(in srgb, var(--scan-ink) 3%, var(--attendee-card-color));
    color: var(--scan-muted);
    font-size: 13px;

    strong {
      color: var(--scan-ink);
      font-size: 15px;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    small {
      display: block;
      margin-top: 1px;
      font-size: 11.5px;
      text-wrap: pretty;
    }
  }

  &__lead {
    margin: 0 2px;
    color: var(--scan-muted);
    font-size: 13px;
    line-height: 1.5;

    // Desktop already says this in the page subtitle.
    @media (min-width: 901px) {
      display: none;
    }
  }

  &__tip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 6px;
    color: var(--scan-muted);
    font-size: 12.5px;
    line-height: 1.4;

    i,
    svg {
      color: var(--scan-brand);
    }
  }

  &__hero {
    padding: 8px 10px 2px;
    text-align: center;
  }

  &__hero-icon {
    display: inline-grid;
    place-items: center;
    width: 56px;
    height: 56px;
    margin-bottom: 10px;
    border-radius: 18px;
    background-color: color-mix(in srgb, var(--scan-brand) 12%, transparent);
    color: var(--scan-brand);
    font-size: 26px;
  }

  &__hero-title {
    margin: 0 0 4px;
    font-size: 19px;
    font-weight: 600;
  }

  &__hero-text {
    max-width: 34ch;
    margin: 0 auto;
    color: var(--scan-muted);
    font-size: 13px;
    line-height: 1.5;
    text-wrap: pretty;
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__skeleton-card {
    height: 150px;
    border-radius: calc(var(--attendee-card-border-radius) + 4px);
    background-color: var(--attendee-card-color);
    animation: attendee-scan-pulse 1.4s ease-in-out infinite;

    &--short {
      height: 90px;
    }
  }

  &__load-error {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: var(--attendee-card-border-radius);
    background-color: color-mix(in srgb, var(--scan-danger) 7%, var(--attendee-card-color));
    color: var(--scan-danger);
    font-size: 14px;
  }

  &__retry {
    margin-left: auto;
    padding: 6px 14px;
    border: 1.5px solid currentColor;
    border-radius: 10px;
    background: transparent;
    color: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  // Always reachable: pinned right above the bottom menu on a phone, a plain
  // row under the cards on desktop.
  &__dock {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: var(--attendee-gap-md);

    @media (max-width: 900px) {
      position: fixed;
      right: 0;
      bottom: calc(var(--attendee-footer-mobile-height) + env(safe-area-inset-bottom));
      left: 0;
      z-index: 99;
      flex-direction: column;
      gap: 8px;
      margin: 0;
      padding: 10px var(--attendee-gap-md) 12px;
      background-image: linear-gradient(to top, var(--attendee-background-color) 70%, transparent);
    }
  }

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 260px;
    min-height: 56px;
    padding: 6px 20px;
    border: 0;
    border-radius: 16px;
    color: white;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;

    @media (max-width: 900px) {
      width: 100%;
      min-width: 0;
    }

    > i {
      font-size: 22px;
    }

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }

    &--in {
      background-color: var(--scan-brand);
      box-shadow: 0 6px 16px color-mix(in srgb, var(--scan-brand) 35%, transparent);
    }

    &--out {
      background-color: var(--scan-live);
      box-shadow: 0 6px 16px color-mix(in srgb, var(--scan-live) 32%, transparent);
    }

    &--secondary {
      min-height: 46px;
      border: 1.5px solid color-mix(in srgb, var(--scan-ink) 14%, transparent);
      background-color: var(--attendee-card-color);
      color: var(--scan-ink);
      font-size: 15px;

      > i {
        font-size: 15px;
      }
    }
  }

  &__action-label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.15;
    text-align: left;
  }

  &__action-sub {
    max-width: 60vw;
    overflow: hidden;
    font-size: 12px;
    font-weight: 500;
    opacity: 0.88;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@keyframes attendee-scan-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
</style>
